// Full-page app widgets: the one part of the product's URL space that is deliberately
// NOT authored as a route.
//
// A widget's route arrives from the slot-assignment API at runtime — it is tenant
// configuration rather than source — so there is nothing to declare in the route
// registry and there will never be a route file for it. The product renders this
// component from its `/apps` splat, `routes/_authenticated/apps/$.tsx`, which ranks behind
// every declared page and passes the URL's pathname in. This component asks
// `matchFullPageAssignment` which widget owns it, and hands the URL back to the product's own
// no-match fallback when none does.
//
// That makes this file the permanent, intentional exception to "every route is authored
// in the routes directory" — see the "Runtime routes: app slots" section of
// `documentation/routing-reference.md`.
//
// It is also why nothing here declares a route: the product's route tree is built from
// files at build time, and a route only known at runtime can be matched but not declared.
// Matching lives in `matchFullPageAssignment`; this component only reads the URL and renders
// the outcome.

import { stringifySearch, useSearch } from '@luminovo/design-system';
import { type ReactElement, type ReactNode, useEffect, useMemo, useRef } from 'react';

import { isAddressable } from '../api/fetchSlotAssignments';
import { useSlotErrorReporting } from '../errors/slotErrorReporting';
import { useAppsSession } from '../hooks/useAppsSession';
import { useSlotAssignments } from '../hooks/useSlotAssignments';
import {
    fullPageCollisionError,
    type FullPageSlotAssignment,
    fullPageWidgetKey,
    matchFullPageAssignment,
    type NoFullPageAssignmentMatch,
} from '../routing/matchFullPageAssignment';
import { SlotContainer } from './SlotContainer';

const NO_WIDGET_ROUTES: NoFullPageAssignmentMatch = { status: 'no-match' };

export interface FullPageSlotsProps {
    loadingFallback?: ReactNode;
    errorFallback?: ReactNode;
    noMatchFallback?: ReactNode;
    /**
     * Shown when the assignments could not be fetched: nobody can tell then whether a widget owns the URL, and
     * `noMatchFallback` would call an app's page missing during an apps-platform outage. Defaults to `errorFallback`.
     */
    unavailableFallback?: ReactNode;
    iframeLoadingFallback?: ReactNode;
    iframeErrorFallback?: ReactNode;
    /** Active host locale used when displaying app-authored widget names. */
    locale: string;
    /** Slug of the tenant the product page is showing, forwarded to every widget. */
    tenant: string;
    /**
     * The loaded URL's path, still encoded (`/apps/hubspot/a%2Fb`), from the host route's match like the query string
     * here, so all three parts come from one URL. Not the router's location: it moves when a navigation away starts,
     * turning the page being left into the not-found page, which reports the URL to Sentry. Not the match's
     * `pathname`: a splat's is rebuilt from the decoded param, turning `%2F` into another segment.
     */
    pathname: string;
    /** The fragment of the same URL, without `#`, decoded. The host reads it from its route's match too. */
    hash: string;
    /**
     * Wraps the content of a MATCHED widget route so the host can put its own
     * chrome — a sidebar, a header — around app pages. The library picks which
     * states deserve chrome; the host stays the only one deciding what the chrome
     * looks like, which is what keeps this package host-agnostic.
     *
     * Applied to the widget frame and, through it, to `iframeLoadingFallback` and
     * `iframeErrorFallback`; also to the per-route `errorFallback`, because a
     * matched route with unusable params is still an app page and should not lose
     * its navigation.
     *
     * Deliberately NOT applied to `noMatchFallback`: that is the product's own
     * "this URL is nothing" page, and dressing it as an app page would claim a
     * route no widget owns. The top-level `loadingFallback`, `errorFallback` and
     * `unavailableFallback` are returned before the matcher runs — there is no
     * matched route to give them chrome for — so they are not wrapped either.
     */
    renderLayout?: (content: ReactNode) => ReactNode;
}

export function FullPageSlots({
    loadingFallback = null,
    errorFallback = null,
    noMatchFallback = null,
    unavailableFallback = errorFallback,
    iframeLoadingFallback,
    iframeErrorFallback,
    locale,
    tenant,
    pathname,
    hash,
    renderLayout = (content) => content,
}: FullPageSlotsProps): ReactElement | null {
    // From the route's match, like `pathname` and `hash`: it moves in the render that shows the next page.
    const search = useSearch({ strict: false, select: stringifySearch });
    // WidgetLocation's hash keeps the `#` (`#foo`); the prop drops it.
    const locationHash = hash ? `#${hash}` : '';
    const { data, isLoading, error } = useSlotAssignments('slots.full-page');
    const appsOrigin = data?.appsOrigin;
    const assignments = data?.assignments;

    const fullPageAssignments = useMemo(
        () =>
            (assignments ?? []).filter(
                (assignment): assignment is FullPageSlotAssignment =>
                    assignment.widget.kind === 'full-page' && isAddressable(assignment),
            ),
        [assignments],
    );

    // Step 2, gating step 3: the session must exist before the first widget frame
    // is created. Skipped entirely when this page has no full-page widget to show,
    // so a product page nobody has extended costs no extra request.
    const appsSession = useAppsSession(fullPageAssignments.length > 0 ? appsOrigin : undefined);

    // No session means no widgets on this page — including the routes they would have
    // owned, so a URL that a widget claims falls through to the product's own no-match
    // handling rather than to a frame that can only render a 401.
    const hasWidgetRoutes =
        appsOrigin !== undefined && fullPageAssignments.length > 0 && appsSession.data?.status === 'ready';

    // Matched on every render, not memoised: the matcher is pure and cheap, and the reason a
    // memo used to be here — keeping a misconfigured tenant to one Sentry event — now lives in
    // the effect below, where it belongs. Skipped entirely when there are no widget routes:
    // reporting a collision between two widgets this page would not have rendered either way
    // would name a winner nobody saw.
    const match = hasWidgetRoutes ? matchFullPageAssignment(fullPageAssignments, pathname, search) : NO_WIDGET_ROUTES;

    const collision = match.status === 'no-match' ? undefined : match.collision;
    // The collision's stable identity. The matcher hands back a fresh object every render, so
    // the effect needs something comparable to depend on; reporting the same competitors for
    // the same URL twice tells a triager nothing new.
    const collisionKey = collision && `${collision.pathname}\n${collision.competingWidgets.join(',')}`;

    const reportError = useSlotErrorReporting();
    const reportedCollision = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!collision || reportedCollision.current === collisionKey) return;
        reportedCollision.current = collisionKey;
        reportError(fullPageCollisionError(collision), {
            level: 'error',
            tags: { slotId: 'slots.full-page' },
            extra: {
                pathname: collision.pathname,
                competingWidgets: collision.competingWidgets,
                renderedWidget: collision.competingWidgets[0],
                competingRoutes: collision.competingRoutes,
            },
        });
        // Keyed on `collisionKey` rather than on `collision` itself — see above. The ref makes
        // that hold for the two cases a dep array cannot: `React.StrictMode` runs every effect
        // twice on mount, and the same collision seen again later is the same misconfiguration.
        // `reportError` comes from a context the host mounts once, so it is stable in practice.
    }, [collisionKey]);

    if (isLoading) {
        return <>{loadingFallback}</>;
    }
    if (error) {
        return <>{errorFallback}</>;
    }
    if (data?.degraded) {
        return <>{unavailableFallback}</>;
    }
    if (appsSession.isLoading) {
        return <>{loadingFallback}</>;
    }
    if (!hasWidgetRoutes || match.status === 'no-match') {
        return <>{noMatchFallback}</>;
    }
    // A matched route whose params are unusable: still an app page, so it keeps the host
    // chrome and shows the host's error instead of being demoted to the no-match page.
    if (match.status === 'invalid-params') {
        return <>{renderLayout(errorFallback)}</>;
    }

    return (
        <>
            {renderLayout(
                <SlotContainer
                    key={fullPageWidgetKey(match.assignment)}
                    assignment={match.assignment}
                    appsOrigin={appsOrigin}
                    slotId="slots.full-page"
                    tenant={tenant}
                    params={match.params}
                    location={{ pathname, search, hash: locationHash }}
                    locale={locale}
                    loadingFallback={iframeLoadingFallback}
                    errorFallback={iframeErrorFallback}
                />,
            )}
        </>
    );
}
